/**
 * Bundle Builder Modal — drop-agnostic
 *
 * Refactored from assets/jerusalem-bundle-modal.js (Apr 2026 Jerusalem launch).
 *
 * Discount code and order-source label now read from data attributes on the
 * modal element, set via the section settings in the theme editor. This means
 * one section + one JS file serves every drop.
 *
 * Handles:
 * - Opening/closing the modal
 * - Selecting/deselecting add-on products
 * - Updating the visual preview (thumbnail strip)
 * - Calculating prices with discount
 * - Adding items to cart (via Shopify AJAX Cart API)
 * - Redirecting to checkout
 * - Applying the discount code in UpCart drawer + via Shopify cookie fallback
 * - Tagging the order with Source + Bundle Items attributes for analytics
 */

(function() {
  'use strict';

  var modals = document.querySelectorAll('[data-bundle-modal]');

  modals.forEach(function(modal) {
    initBundleModal(modal);
  });

  /**
   * Recursively search the DOM (including shadow roots) for a selector.
   * UpCart renders inside a shadow DOM, so document.querySelector alone
   * won't find its discount input.
   */
  function deepShadowQuery(root, selector) {
    if (!root) return null;
    try {
      var match = root.querySelector ? root.querySelector(selector) : null;
      if (match) return match;
    } catch (e) { /* invalid selector for this root, continue */ }

    var all = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) {
        var found = deepShadowQuery(all[i].shadowRoot, selector);
        if (found) return found;
      }
    }
    return null;
  }

  function findDiscountInput() {
    var selectors = [
      'input[placeholder="Discount code"]',
      'input[placeholder*="iscount"]',
      '[data-discount-input] input',
      'form[action*="discount"] input[type="text"]'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = deepShadowQuery(document, selectors[i]);
      if (el && el.tagName === 'INPUT') return el;
    }
    return null;
  }

  function findApplyButton(input) {
    var root = input.getRootNode();
    var candidates = root.querySelectorAll('button, [role="button"]');
    for (var i = 0; i < candidates.length; i++) {
      var text = (candidates[i].textContent || '').trim().toLowerCase();
      if (text === 'apply' || text === 'apply code') {
        return candidates[i];
      }
    }
    var form = input.closest('form');
    if (form) {
      var submit = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submit) return submit;
    }
    return null;
  }

  /**
   * Apply the discount code in the UpCart drawer. Polls for up to 6 seconds
   * because UpCart mounts asynchronously after /cart/add.js resolves.
   * Silently no-ops if it can't find the elements — the discount cookie
   * fallback (set separately) ensures checkout still gets the discount.
   */
  function applyDiscountInUpCart(code) {
    if (!code) return;
    var attempts = 0;
    var maxAttempts = 30;

    var interval = setInterval(function() {
      attempts++;
      var input = findDiscountInput();

      if (input) {
        clearInterval(interval);

        if ((input.value || '').toUpperCase() === code.toUpperCase()) return;

        var proto = window.HTMLInputElement && window.HTMLInputElement.prototype;
        var setter = proto && Object.getOwnPropertyDescriptor(proto, 'value');
        if (setter && setter.set) {
          setter.set.call(input, code);
        } else {
          input.value = code;
        }

        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));

        setTimeout(function() {
          var btn = findApplyButton(input);
          if (btn) btn.click();
        }, 150);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 200);
  }

  function initBundleModal(modal) {
    var discountPct = parseInt(modal.dataset.discount, 10) / 100;
    var discountCode = modal.dataset.discountCode || '';
    var sourceLabel = modal.dataset.sourceLabel || 'Bundle Modal';

    // Currency symbol for multi-currency display (defaults to $ for USD shops)
    var currencySymbol = modal.dataset.currencySymbol || '$';

    // Free shipping threshold is in SHOP'S BASE CURRENCY (USD for OO, in cents).
    // We convert it to the customer's local currency below using Shopify.currency.rate.
    var shippingThresholdBaseCents = parseInt(modal.dataset.shippingThresholdCents, 10) || 0;
    var shippingProgressEnabled = modal.dataset.shippingProgressEnabled === 'true';

    // Shopify.currency.rate converts shop base currency → customer's display currency.
    // For US customers (or single-currency shops), rate is 1.
    // For UK customers, rate ≈ 0.79 (1 USD = 0.79 GBP), so $110 USD ≈ £86.90 GBP threshold.
    var currencyRate = 1;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
      var parsedRate = parseFloat(window.Shopify.currency.rate);
      if (!isNaN(parsedRate) && parsedRate > 0) currencyRate = parsedRate;
    }
    var shippingThresholdLocalCents = Math.round(shippingThresholdBaseCents * currencyRate);

    var anchorPriceEl = modal.querySelector('[data-anchor-price]');
    var anchorPrice = anchorPriceEl ? parseInt(anchorPriceEl.dataset.anchorPrice, 10) : 0;

    var addons = modal.querySelectorAll('[data-bbm-addon]');
    var previewContainer = modal.querySelector('[data-bbm-preview]');
    var itemCountEl = modal.querySelector('[data-bbm-item-count]');
    var originalEl = modal.querySelector('[data-bbm-original]');
    var saveEl = modal.querySelector('[data-bbm-save]');
    var finalEl = modal.querySelector('[data-bbm-final]');
    var checkoutBtn = modal.querySelector('[data-bbm-checkout]');
    var addToCartBtn = modal.querySelector('[data-bbm-addtocart]');
    var closeBtn = modal.querySelector('[data-bbm-close]');

    // Free shipping progress elements (may not exist if section setting is off)
    var shippingProgressEl = modal.querySelector('[data-bbm-shipping-progress]');
    var shippingFillEl = modal.querySelector('[data-bbm-shipping-fill]');
    var shippingRemainingEl = modal.querySelector('[data-bbm-shipping-remaining]');
    var shippingProgressTextEl = modal.querySelector('[data-bbm-shipping-progress-text]');
    var shippingUnlockedTextEl = modal.querySelector('[data-bbm-shipping-unlocked-text]');

    var triggers = document.querySelectorAll('[data-bundle-trigger]');
    triggers.forEach(function(trigger) {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        openModal();
      });
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display !== 'none') {
        closeModal();
      }
    });

    addons.forEach(function(addon) {
      var qtySelect = addon.querySelector('[data-bbm-qty]');

      addon.addEventListener('click', function(e) {
        if (e.target.closest('[data-bbm-qty]')) return;

        if (addon.classList.contains('bbm-addon--selected')) {
          addon.classList.remove('bbm-addon--selected');
          if (qtySelect) qtySelect.value = '1';
        } else {
          addon.classList.add('bbm-addon--selected');
          if (qtySelect) qtySelect.value = '1';
        }
        updateState();
      });

      if (qtySelect) {
        qtySelect.addEventListener('change', function() {
          var qty = parseInt(qtySelect.value, 10) || 0;
          if (qty <= 0) {
            addon.classList.remove('bbm-addon--selected');
            qtySelect.value = '1';
          } else {
            addon.classList.add('bbm-addon--selected');
          }
          updateState();
        });

        qtySelect.addEventListener('click', function(e) {
          e.stopPropagation();
        });
      }
    });

    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', function() {
        addItemsToCart(true);
      });
    }

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', function() {
        addItemsToCart(false);
      });
    }

    function openModal() {
      modal.style.display = 'flex';
      document.body.classList.add('bbm-modal-open');
      updateState();
    }

    function closeModal() {
      modal.style.display = 'none';
      document.body.classList.remove('bbm-modal-open');
    }

    function getSelectedAddons() {
      var selected = [];
      addons.forEach(function(addon) {
        if (addon.classList.contains('bbm-addon--selected')) {
          var qtyEl = addon.querySelector('[data-bbm-qty]');
          var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
          selected.push({
            variantId: addon.dataset.addonVariantId,
            price: parseInt(addon.dataset.addonPrice, 10),
            title: addon.dataset.addonTitle,
            image: addon.dataset.addonImage,
            quantity: qty
          });
        }
      });
      return selected;
    }

    function updateState() {
      var selected = getSelectedAddons();
      var addonUnitCount = 0;
      var originalTotal = anchorPrice;

      selected.forEach(function(item) {
        addonUnitCount += item.quantity;
        originalTotal += item.price * item.quantity;
      });

      var totalItems = 1 + addonUnitCount;
      var discountAmount = Math.round(originalTotal * discountPct);
      var finalTotal = originalTotal - discountAmount;
      var hasAddons = selected.length > 0;

      if (itemCountEl) {
        itemCountEl.textContent = totalItems;
      }

      if (originalEl) {
        originalEl.textContent = formatMoney(originalTotal);
        originalEl.style.display = hasAddons ? '' : 'none';
      }

      if (saveEl) {
        if (hasAddons) {
          saveEl.textContent = 'You save ' + formatMoney(discountAmount) + ' (' + modal.dataset.discount + '%)';
        } else {
          saveEl.textContent = 'Select items to see savings';
        }
      }

      if (finalEl) {
        finalEl.textContent = formatMoney(hasAddons ? finalTotal : anchorPrice);
      }

      if (checkoutBtn) {
        checkoutBtn.disabled = !hasAddons;
        checkoutBtn.textContent = hasAddons
          ? 'Checkout — ' + formatMoney(finalTotal)
          : 'Checkout';
      }

      if (addToCartBtn) {
        addToCartBtn.disabled = !hasAddons;
      }

      updateShippingProgress(hasAddons ? finalTotal : anchorPrice);
      updatePreview(selected);
    }

    function updateShippingProgress(currentTotal) {
      if (!shippingProgressEnabled || !shippingProgressEl || shippingThresholdLocalCents <= 0) {
        return;
      }
      var remaining = shippingThresholdLocalCents - currentTotal;
      var pctFilled = Math.min(100, Math.max(0, (currentTotal / shippingThresholdLocalCents) * 100));

      if (shippingFillEl) {
        shippingFillEl.style.width = pctFilled + '%';
      }

      if (remaining > 0) {
        if (shippingRemainingEl) {
          shippingRemainingEl.textContent = formatMoney(remaining);
        }
        if (shippingProgressTextEl) shippingProgressTextEl.style.display = '';
        if (shippingUnlockedTextEl) shippingUnlockedTextEl.style.display = 'none';
        if (shippingProgressEl) shippingProgressEl.classList.remove('bbm-shipping-progress--unlocked');
      } else {
        if (shippingProgressTextEl) shippingProgressTextEl.style.display = 'none';
        if (shippingUnlockedTextEl) shippingUnlockedTextEl.style.display = '';
        if (shippingProgressEl) shippingProgressEl.classList.add('bbm-shipping-progress--unlocked');
      }
    }

    function updatePreview(selected) {
      if (!previewContainer) return;

      var anchorThumb = previewContainer.querySelector('.bbm-preview__thumb--anchor');
      previewContainer.innerHTML = '';

      if (anchorThumb) {
        previewContainer.appendChild(anchorThumb);
      }

      var totalUnits = 0;
      selected.forEach(function(item) {
        for (var q = 0; q < item.quantity; q++) {
          totalUnits++;

          var plus = document.createElement('span');
          plus.className = 'bbm-preview__plus';
          plus.textContent = '+';
          previewContainer.appendChild(plus);

          var thumb = document.createElement('div');
          thumb.className = 'bbm-preview__thumb bbm-preview__thumb--selected';
          if (item.image) {
            var img = document.createElement('img');
            img.src = item.image;
            img.alt = item.title;
            img.width = 36;
            img.height = 48;
            thumb.appendChild(img);
          } else {
            thumb.textContent = item.title.substring(0, 3);
          }
          previewContainer.appendChild(thumb);
        }
      });

      var remaining = 5 - totalUnits;
      for (var i = 0; i < Math.min(remaining, 2); i++) {
        var plus2 = document.createElement('span');
        plus2.className = 'bbm-preview__plus';
        plus2.textContent = '+';
        previewContainer.appendChild(plus2);

        var empty = document.createElement('div');
        empty.className = 'bbm-preview__empty';
        empty.textContent = '+';
        previewContainer.appendChild(empty);
      }

      var count = document.createElement('span');
      count.className = 'bbm-preview__count';
      count.textContent = (1 + totalUnits) + ' items selected';
      previewContainer.appendChild(count);
    }

    function buildItemsList() {
      var selected = getSelectedAddons();
      if (selected.length === 0) return [];

      var items = [];

      var anchorVariantInput = document.querySelector('[name="id"][value]');
      var anchorVariantId = anchorVariantInput ? anchorVariantInput.value : null;

      if (anchorVariantId) {
        items.push({
          id: parseInt(anchorVariantId, 10),
          quantity: 1
        });
      }

      selected.forEach(function(item) {
        items.push({
          id: parseInt(item.variantId, 10),
          quantity: item.quantity
        });
      });

      return items;
    }

    function addItemsToCart(goToCheckout) {
      var items = buildItemsList();
      if (items.length === 0) return;

      if (checkoutBtn) checkoutBtn.disabled = true;
      if (addToCartBtn) addToCartBtn.disabled = true;

      if (goToCheckout) {
        // Form submission (NOT AJAX) so UpCart can't intercept and pop the
        // cart drawer. Adds items + redirects in one full-page navigation
        // straight to /checkout with the discount applied.
        var form = document.createElement('form');
        form.method = 'POST';
        form.action = '/cart/add';
        form.style.display = 'none';

        items.forEach(function(item) {
          var idInput = document.createElement('input');
          idInput.type = 'hidden';
          idInput.name = 'id[]';
          idInput.value = item.id;
          form.appendChild(idInput);

          var qtyInput = document.createElement('input');
          qtyInput.type = 'hidden';
          qtyInput.name = 'quantity[]';
          qtyInput.value = item.quantity;
          form.appendChild(qtyInput);
        });

        var sourceAttr = document.createElement('input');
        sourceAttr.type = 'hidden';
        sourceAttr.name = 'attributes[Source]';
        sourceAttr.value = sourceLabel;
        form.appendChild(sourceAttr);

        var bundleSizeAttr = document.createElement('input');
        bundleSizeAttr.type = 'hidden';
        bundleSizeAttr.name = 'attributes[Bundle Items]';
        bundleSizeAttr.value = items.length;
        form.appendChild(bundleSizeAttr);

        var returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        returnTo.value = discountCode
          ? '/checkout?discount=' + encodeURIComponent(discountCode)
          : '/checkout';
        form.appendChild(returnTo);

        document.body.appendChild(form);
        form.submit();
        return;
      }

      // Add-to-cart flow (AJAX): adds items, fires UpCart drawer, attaches
      // tracking attributes, applies discount via cookie + UpCart UI fill.
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        closeModal();

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Source': sourceLabel,
              'Bundle Items': String(items.length)
            }
          })
        }).catch(function() { /* non-blocking */ });

        if (discountCode) {
          fetch('/discount/' + encodeURIComponent(discountCode), {
            method: 'GET',
            credentials: 'same-origin'
          }).catch(function() { /* non-blocking */ });
        }

        document.dispatchEvent(new CustomEvent('cart:updated'));
        if (typeof theme !== 'undefined' && typeof theme.cart !== 'undefined' && typeof theme.cart.getCart === 'function') {
          theme.cart.getCart();
        }
        if (typeof upcart !== 'undefined') {
          try { upcart.refreshCart(); } catch(e) {}
        }

        applyDiscountInUpCart(discountCode);
      })
      .catch(function(error) {
        console.error('Bundle add to cart error:', error);
        updateState();
      });
    }

    function formatMoney(cents) {
      return currencySymbol + (cents / 100).toFixed(2);
    }
  }
})();
